import apiClient from '../../shared/api/client';

export interface ProfileCourseEnrollment {
  id: number;
  status: string;
  purchased_at: string | null;
  course: { id: number; name: string; division: string; delivery_type: string };
}

export interface ProfileEventRegistration {
  id: number;
  status: string;
  registered_at: string;
  event: {
    id: number;
    display_title: string;
    starts_at: string;
    ends_at: string | null;
    timezone: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    format: string;
    location: string | null;
    virtual_url: string | null;
    venue: { id: number; name: string; address: string | null } | null;
  };
  course: { id: number; name: string; division: string };
}

export interface UserProfile {
  id: number;
  email_address: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  auth0_uid: string;
  role: string;
  created_at: string;
  title: string | null;
  company_name: string | null;
  /** The learner's organization in HRA, which stands in for a company. */
  organization_name?: string | null;
  phone: string | null;
  shipping_line1: string | null;
  shipping_line2: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_postal_code: string | null;
  shipping_country: string | null;
  profile_complete: boolean;
  shipping_address_present: boolean;
  thinkific_logout_url: string;
  has_instructor_profile: boolean;
  memberships?: ProfileMembership[];
  courses?: ProfileCourseEnrollment[];
  event_registrations?: ProfileEventRegistration[];
}

export interface ProfileUpdatePayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  title?: string;
  company_name?: string;
  phone?: string;
  shipping_line1?: string;
  shipping_line2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_postal_code?: string;
  shipping_country?: string;
}

export interface ProfileMembership {
  id: number;
  role: string;
  accepted_at: string | null;
  organization: {
    id: number;
    name: string;
    slug: string;
  };
}

export interface InvitationInfo {
  email: string;
  organization: string;
  role: string;
  status: string;
}

export interface AcceptInvitationResponse {
  message: string;
  organization?: string;
}

export const profileApi = {
  get: async (): Promise<UserProfile> => {
    const { data } = await apiClient.get('/me');
    return data;
  },

  update: async (params: ProfileUpdatePayload): Promise<UserProfile> => {
    const { data } = await apiClient.patch('/me', params);
    return data;
  },

  register: async (params: { email?: string; first_name?: string; last_name?: string }): Promise<UserProfile> => {
    const { data } = await apiClient.post('/users', params);
    return data;
  },

  // Re-sends the Auth0 email-verification message to the signed-in (but not yet
  // verified) user. Backs the "Resend" button on VerifyEmailScreen.
  resendVerification: async (): Promise<{ message: string }> => {
    const { data } = await apiClient.post('/me/resend_verification');
    return data;
  },
};

export interface LinkedAccount {
  id: number;
  email_address: string;
  full_name: string;
  linked_at: string;
}

export const linkedAccountsApi = {
  list: async (): Promise<LinkedAccount[]> => {
    const { data } = await apiClient.get('/me/linked_accounts');
    return data.result || [];
  },

  create: async (emailAddress: string): Promise<LinkedAccount> => {
    const { data } = await apiClient.post('/me/linked_accounts', { email_address: emailAddress });
    return data;
  },

  destroy: async (id: number): Promise<void> => {
    await apiClient.delete(`/me/linked_accounts/${id}`);
  },
};

export interface StartInvitationResponse {
  action: 'signup' | 'login';
  email: string;
}

export const invitationApi = {
  get: async (token: string): Promise<InvitationInfo> => {
    const { data } = await apiClient.get(`/invitations/${encodeURIComponent(token)}`);
    return data;
  },

  /** Maps the invitee's row to its Auth0 identity and says which Auth0 screen
   *  they need, before we hand them over. Address comes from the signed token. */
  start: async (token: string): Promise<StartInvitationResponse> => {
    const { data } = await apiClient.post(`/invitations/${encodeURIComponent(token)}/start`);
    return data;
  },

  accept: async (token: string): Promise<AcceptInvitationResponse> => {
    const { data } = await apiClient.post(`/invitations/${encodeURIComponent(token)}/accept`);
    return data;
  },
};
